import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import DynamicIsland from '../components/DynamicIsland';
import Footer from '../components/Footer';
import { ComuneAutocomplete } from '../components/ComuneAutocomplete';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Phone, User, Mail, CheckCircle2, ShieldCheck, Award, Clock, Search, ClipboardCheck, Wrench, Star, MessageCircle, AlertCircle, Droplets, Layers, Home } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getLeadTrackingFields } from '../utils/leadTracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WA_LINK = `https://wa.me/393791617599?text=${encodeURIComponent('Salve, ho subito una grandinata e vorrei far verificare il mio tetto.')}`;

const problemi = [
  'Tegole/coppi danneggiati',
  'Possibili infiltrazioni',
  'Danni da grandine',
  'Copertura deteriorata',
  'Voglio verificare lo stato del tetto',
  'Altro',
];

const problemBlocks = [
  { icon: Home, title: 'Tegole o coppi danneggiati', desc: 'Gli impatti della grandine possono rompere o scheggiare tegole e coppi, anche in punti non visibili da terra.' },
  { icon: AlertCircle, title: 'Crepe e rotture', desc: 'Microfratture sugli elementi della copertura possono allargarsi nel tempo con pioggia, gelo e sbalzi termici.' },
  { icon: Droplets, title: 'Possibili infiltrazioni', desc: 'Un elemento danneggiato può lasciar passare acqua: il problema a volte si manifesta solo dopo le piogge successive.' },
  { icon: Layers, title: 'Deterioramento della copertura', desc: 'Grandinate ripetute accelerano l\u2019usura del manto, delle sigillature e delle converse.' },
];

const steps = [
  { num: '01', icon: Search, title: 'Analisi della copertura', desc: 'Il tecnico valuta lo stato del tetto e le principali criticità.' },
  { num: '02', icon: ClipboardCheck, title: 'Individuazione dei danni', desc: 'Vengono analizzati gli elementi della copertura e gli eventuali problemi presenti.' },
  { num: '03', icon: Wrench, title: 'Proposta di intervento', desc: 'Se necessario, viene proposta la soluzione più adatta al caso specifico.' },
];

const trustItems = [
  { icon: Award, label: '15+ anni di esperienza' },
  { icon: CheckCircle2, label: '2.500+ clienti' },
  { icon: Star, label: '4,9/5 valutazione media' },
  { icon: ShieldCheck, label: 'Tecnici certificati' },
  { icon: ShieldCheck, label: 'Assicurazione RC' },
  { icon: Clock, label: 'Intervento in 48h' },
];

const testimonials = [
  { name: 'Elena T.', location: 'Treviso', role: 'Proprietaria', text: 'Dopo la grandine avevo il tetto distrutto. Risolto tutto in 3 giorni senza ponteggi. Prezzo onesto e lavoro a regola d\u2019arte.', highlight: true },
  { name: 'Marco R.', location: 'Verona', role: 'Proprietario', text: 'Avevo 3 preventivi, tutti con ponteggi sopra i 10.000 euro. Evoluzione Tetto ha risolto in 2 giorni, spendendo meno della metà.', highlight: false },
  { name: 'Laura B.', location: 'Padova', role: 'Amministratore', text: 'Infiltrazioni risolte definitivamente dopo anni di rattoppi. Intervento pulito, veloce e professionale. La garanzia scritta ci ha dato sicurezza.', highlight: false },
];

const lavori = [
  { src: '/grandine/prima-dopo-reale.jpg', caption: 'Prima / dopo: ripasso completo della copertura con sostituzione degli elementi danneggiati.' },
  { src: '/grandine/lavori-reali-1.jpg', caption: 'La nostra squadra al lavoro in sicurezza con caschi, imbracature e linee vita.' },
  { src: '/grandine/lavori-reali-2.jpg', caption: 'Sostituzione coppi e sistemazione del manto direttamente in copertura.' },
];

const GrandinePage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', phone: '', email: '', comune: '', provincia: '', problema: '', website: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const prevTitle = document.title;
    document.title = 'Danni da Grandine al Tetto | Sopralluogo Gratuito | Evoluzione Tetto';
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    const prevDesc = meta.content;
    meta.content = 'Hai subito una grandinata? Fai verificare lo stato del tuo tetto. Evoluzione Tetto offre sopralluoghi gratuiti per valutare eventuali danni alla copertura.';
    return () => { document.title = prevTitle; if (created) meta.remove(); else meta.content = prevDesc; };
  }, []);

  useEffect(() => {
    if (!formRef.current) return;
    const obs = new IntersectionObserver(([e]) => setFormVisible(e.isIntersecting), { threshold: 0.2 });
    obs.observe(formRef.current);
    return () => obs.disconnect();
  }, []);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.comune || !form.problema) { toast.error('Compila i campi obbligatori'); return; }
    if (form.comune.trim().length < 3) { toast.error('Inserisci il nome completo del comune'); return; }
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/leads`, {
        name: form.name,
        phone: form.phone,
        email: form.email || '',
        province: form.provincia || 'Da definire',
        houseType: 'Da definire',
        ownershipStatus: 'Da definire',
        problemDescription: `Landing Grandine | Problema: ${form.problema} | Comune immobile: ${form.comune}`,
        tipoIntervento: 'Verifica danni grandine',
        comuneImmobile: form.comune,
        website: form.website,
        ...getLeadTrackingFields(),
        citta: form.comune,
      });
      if (typeof window.gtag === 'function') window.gtag('event', 'conversion', { send_to: 'AW-17960264240/HNy_CMqrr_obELDEj_RC', value: 1.0, currency: 'EUR' });
      if (typeof window.fbq === 'function') window.fbq('track', 'Lead', { content_name: 'Landing Grandine', content_category: 'Grandine' });
      navigate('/grazie');
    } catch { toast.error("Errore nell'invio. Riprova."); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="grandine-page">
      <DynamicIsland onOpenForm={scrollToForm} />

      {/* HERO */}
      <section className="relative bg-[#0F172A] overflow-hidden pt-28 pb-16 lg:pt-36 lg:pb-24">
        <div className="absolute inset-0">
          <img src="/grandine/tetto-dopo-grandinata.jpg" alt="Tetto dopo una grandinata" className="w-full h-full object-cover opacity-15" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/80 via-[#0F172A]/90 to-[#0F172A]" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-7 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 mb-5 px-4 py-2 bg-green-500/15 border border-green-500/25 rounded-full">
              <ShieldCheck className="w-4 h-4 text-green-400" />
              <span className="text-green-400 font-medium text-sm">Verifica della copertura dopo eventi atmosferici</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-5 leading-[1.08] text-white" data-testid="grandine-h1">
              Grandinata? <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-300">Fai controllare il tuo tetto.</span>
            </h1>
            <p className="text-lg text-slate-300 mb-7 leading-relaxed max-w-xl mx-auto lg:mx-0">
              I danni alla copertura non sono sempre evidenti da terra. Richiedi una verifica del tetto e scopri se sono necessari interventi.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-7">
              <button data-testid="grandine-hero-cta" onClick={scrollToForm} className="bg-green-600 hover:bg-green-500 text-white font-bold text-base px-7 py-4 rounded-xl transition-all duration-300 shadow-[0_4px_20px_rgba(22,163,74,0.35)] hover:-translate-y-0.5">
                Richiedi il Sopralluogo Gratuito
              </button>
              <a href="tel:800864621" data-testid="grandine-hero-phone" className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 border border-white/15 text-white font-bold text-base px-7 py-4 rounded-xl transition-all duration-300">
                <Phone className="w-4 h-4 text-green-400" /> Chiama 800 86 46 21
              </a>
            </div>
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 text-slate-300 text-sm">
              {['Sopralluogo gratuito', 'Tecnici qualificati', 'Assicurazione RC', 'Senza ponteggi quando tecnicamente possibile'].map((t) => (
                <div key={t} className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" /><span>{t}</span></div>
              ))}
            </div>
          </motion.div>

          {/* FORM */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="lg:col-span-5" ref={formRef} id="form-grandine">
            <div className="bg-white rounded-2xl p-7 shadow-2xl shadow-black/10">
              <div className="text-center mb-5">
                <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Richiedi il Sopralluogo Gratuito</h2>
                <p className="text-slate-500 text-sm mt-1">Ti ricontattiamo entro 24 ore. Nessun impegno.</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-3.5" data-testid="grandine-form">
                <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }} />
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input data-testid="grandine-form-name" type="text" placeholder="Il tuo nome *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="pl-10 h-12 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-xl focus:ring-2 focus:ring-green-600 focus:border-transparent" />
                </div>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input data-testid="grandine-form-phone" type="tel" placeholder="Numero di telefono *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required className="pl-10 h-12 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-xl focus:ring-2 focus:ring-green-600 focus:border-transparent" />
                </div>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input data-testid="grandine-form-email" type="email" placeholder="La tua email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="pl-10 h-12 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-xl focus:ring-2 focus:ring-green-600 focus:border-transparent" />
                </div>
                <ComuneAutocomplete onSelect={({ comune, provincia }) => setForm((prev) => ({ ...prev, comune, provincia }))} />
                <Select value={form.problema} onValueChange={(v) => setForm({ ...form, problema: v })}>
                  <SelectTrigger data-testid="grandine-form-problema" className="h-12 bg-slate-50 border-slate-200 text-slate-900 rounded-xl data-[placeholder]:text-slate-500">
                    <SelectValue placeholder="Qual è il problema? *" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {problemi.map((p) => (<SelectItem key={p} value={p} className="text-slate-900">{p}</SelectItem>))}
                  </SelectContent>
                </Select>
                <button data-testid="grandine-form-submit" type="submit" disabled={isSubmitting} className="w-full bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white font-bold text-base py-3.5 rounded-xl transition-all duration-300 shadow-[0_4px_20px_rgba(22,163,74,0.3)] hover:-translate-y-0.5">
                  {isSubmitting ? 'Invio in corso...' : 'Richiedi il Sopralluogo Gratuito'}
                </button>
              </form>
              <div className="mt-4 space-y-1.5">
                <div className="flex items-center gap-2 text-xs text-slate-500"><CheckCircle2 className="w-3.5 h-3.5 text-green-600" /><span>100% gratuito e senza impegno</span></div>
                <div className="flex items-center gap-2 text-xs text-slate-500"><CheckCircle2 className="w-3.5 h-3.5 text-green-600" /><span>I tuoi dati sono al sicuro (GDPR compliant)</span></div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-center gap-4">
                <a href="tel:800864621" className="flex items-center gap-1.5 text-sm font-bold text-green-700 hover:text-green-600"><Phone className="w-4 h-4" /> 800 86 46 21</a>
                <a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="grandine-form-whatsapp" className="flex items-center gap-1.5 text-sm font-bold text-green-700 hover:text-green-600"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SEZIONE 2 - IL PROBLEMA */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <span className="text-green-700 text-sm font-semibold uppercase tracking-wider">Danni da grandine</span>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-3 max-w-3xl mx-auto">Dopo una grandinata, cosa può essere successo al tuo tetto?</h2>
            <p className="mt-4 text-slate-500 max-w-2xl mx-auto">Una grandinata può danneggiare alcuni elementi della copertura. In alcuni casi i problemi si manifestano anche a distanza di tempo.</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {problemBlocks.map((b, i) => (
              <motion.div key={b.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center mb-4"><b.icon className="w-6 h-6 text-green-700" /></div>
                <h3 className="font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>{b.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <button data-testid="grandine-problema-cta" onClick={scrollToForm} className="bg-green-600 hover:bg-green-500 text-white font-bold px-8 py-3.5 rounded-full transition-all duration-300 shadow-[0_4px_20px_rgba(22,163,74,0.25)] hover:-translate-y-0.5">Fai Verificare il Tuo Tetto</button>
          </div>
        </div>
      </section>

      {/* SEZIONE 3 - HOOK VISIVO */}
      <section className="relative bg-[#0F172A] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <img src="/grandine/tegola-danneggiata-grandine.jpg" alt="Tegola danneggiata dalla grandine con crepe e scheggiature" className="rounded-2xl shadow-2xl w-full max-h-[520px] object-cover" loading="lazy" />
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="text-center lg:text-left">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Outfit' }}>Il problema non sempre si vede da terra.</h2>
            <p className="text-slate-300 text-lg leading-relaxed mb-8">Una verifica della copertura permette di valutare lo stato reale del tetto e individuare eventuali criticità.</p>
            <button data-testid="grandine-hook-cta" onClick={scrollToForm} className="bg-green-600 hover:bg-green-500 text-white font-bold px-8 py-4 rounded-xl transition-all duration-300 shadow-[0_4px_20px_rgba(22,163,74,0.35)] hover:-translate-y-0.5">Richiedi una Verifica</button>
          </motion.div>
        </div>
      </section>

      {/* SEZIONE 4 - COSA FACCIAMO */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <span className="text-green-700 text-sm font-semibold uppercase tracking-wider">Il metodo</span>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-3">Cosa succede durante il sopralluogo?</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <motion.div key={s.num} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-white rounded-2xl p-7 border border-slate-200 relative overflow-hidden">
                <span className="absolute -top-3 -right-2 text-7xl font-extrabold text-green-600/10" style={{ fontFamily: 'Outfit' }}>{s.num}</span>
                <div className="w-12 h-12 rounded-xl bg-green-600 flex items-center justify-center mb-4"><s.icon className="w-6 h-6 text-white" /></div>
                <h3 className="font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>{s.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <button data-testid="grandine-metodo-cta" onClick={scrollToForm} className="bg-green-600 hover:bg-green-500 text-white font-bold px-8 py-3.5 rounded-full transition-all duration-300 shadow-[0_4px_20px_rgba(22,163,74,0.25)] hover:-translate-y-0.5">Richiedi il Sopralluogo Gratuito</button>
          </div>
        </div>
      </section>

      {/* SEZIONE 5 - PERCHÉ EVOLUZIONE TETTO */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-green-700 text-sm font-semibold uppercase tracking-wider">Chi siamo</span>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-3 mb-8">Perché affidarti a Evoluzione Tetto?</h2>
            <div className="grid grid-cols-2 gap-4">
              {trustItems.map((t) => (
                <div key={t.label} className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100">
                  <t.icon className="w-5 h-5 text-green-700 shrink-0" />
                  <span className="text-sm font-medium text-slate-700">{t.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
            <img src="/grandine/furgone-evoluzione-tetto.jpg" alt="Furgone Evoluzione Tetto sul territorio" className="rounded-2xl shadow-xl w-full object-cover" loading="lazy" />
            <p className="text-xs text-slate-400 mt-2 text-center">La nostra squadra operativa sul territorio.</p>
          </motion.div>
        </div>
      </section>

      {/* SEZIONE 6 - LAVORI REALI */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <span className="text-green-700 text-sm font-semibold uppercase tracking-wider">Lavori reali</span>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-3">Guarda alcuni dei nostri interventi</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {lavori.map((l, i) => (
              <motion.figure key={l.src} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
                <img src={l.src} alt={l.caption} className="w-full h-56 object-cover" loading="lazy" />
                <figcaption className="p-4 text-sm text-slate-600 leading-relaxed">{l.caption}</figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* SEZIONE 7 - TESTIMONIANZE */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <span className="text-green-700 text-sm font-semibold uppercase tracking-wider">Testimonianze verificate</span>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-3">Cosa dicono i nostri clienti</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div key={t.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className={`rounded-2xl p-6 border transition-all duration-300 ${t.highlight ? 'bg-green-50 border-green-200 shadow-lg shadow-green-100' : 'bg-white border-slate-200 hover:shadow-lg'}`}>
                {t.highlight && <span className="inline-block mb-3 px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-full">Caso reale — danni da grandine</span>}
                <div className="flex gap-1 mb-4">{Array.from({ length: 5 }).map((_, j) => (<Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />))}</div>
                <p className="text-slate-600 leading-relaxed mb-5 text-sm">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-600/10 flex items-center justify-center text-green-700 font-bold text-sm">{t.name.split(' ').map((n) => n[0]).join('')}</div>
                  <div><div className="text-slate-900 font-medium text-sm">{t.name}</div><div className="text-slate-400 text-xs">{t.role}, {t.location}</div></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SEZIONE 8 - CTA FINALE */}
      <section className="py-16 md:py-24 bg-[#0F172A]">
        <div className="max-w-4xl mx-auto px-6 md:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Outfit' }}>Hai avuto una grandinata?</h2>
            <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">Non aspettare che un eventuale problema diventi un'infiltrazione. Richiedi una valutazione della tua copertura.</p>
            <button data-testid="grandine-final-cta" onClick={scrollToForm} className="bg-green-600 hover:bg-green-500 text-white font-bold text-lg px-10 py-4 rounded-xl transition-all duration-300 shadow-[0_4px_25px_rgba(22,163,74,0.4)] hover:-translate-y-0.5">Richiedi il Sopralluogo Gratuito</button>
            <p className="text-slate-400 text-sm mt-3">Nessun impegno.</p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-5 text-white">
              <a href="tel:800864621" className="flex items-center gap-2 font-bold hover:text-green-400 transition-colors"><Phone className="w-4 h-4 text-green-400" /> 800 86 46 21 <span className="text-slate-400 text-xs font-normal">Numero Verde Gratuito</span></a>
              <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 font-bold hover:text-green-400 transition-colors"><MessageCircle className="w-4 h-4 text-green-400" /> Scrivi su WhatsApp</a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />

      {/* Spacer per sticky bar mobile */}
      <div className="h-16 lg:hidden" />

      {/* STICKY CTA MOBILE */}
      {!formVisible && (
        <div data-testid="grandine-sticky-bar" className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
          <div className="grid grid-cols-3 gap-px bg-slate-200">
            <a href="tel:800864621" data-testid="sticky-call" className="flex flex-col items-center justify-center gap-0.5 bg-white py-2.5 text-slate-800 active:bg-slate-50">
              <Phone className="w-4 h-4 text-green-700" /><span className="text-[11px] font-bold">Chiama</span>
            </a>
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="sticky-whatsapp" className="flex flex-col items-center justify-center gap-0.5 bg-white py-2.5 text-slate-800 active:bg-slate-50">
              <MessageCircle className="w-4 h-4 text-green-700" /><span className="text-[11px] font-bold">WhatsApp</span>
            </a>
            <button onClick={scrollToForm} data-testid="sticky-form" className="flex flex-col items-center justify-center gap-0.5 bg-green-600 py-2.5 text-white active:bg-green-500">
              <ClipboardCheck className="w-4 h-4" /><span className="text-[11px] font-bold">Sopralluogo</span>
            </button>
          </div>
        </div>
      )}

      {/* WhatsApp flottante solo desktop */}
      <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="hidden lg:flex fixed bottom-6 right-6 z-40 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl transition-all hover:scale-110" data-testid="whatsapp-button-grandine">
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>
    </div>
  );
};

export default GrandinePage;
